import { useLayoutEffect, useState, type RefObject } from "react";

/** The side a popover opens on: beside its trigger, or under it. */
export type PopoverSide = "right" | "bottom";

/** The room a popover has beside its trigger, in viewport pixels. */
export interface BesideRoom {
    /** Where the trigger's right edge stands. */
    triggerRight: number;
    /**
     * How wide the viewport is: `document.documentElement.clientWidth`, which
     * is the width Radix measures collisions against (a classic scrollbar is
     * not part of it).
     */
    viewportWidth: number;
    /** How wide the popover is drawn beside the trigger. */
    contentWidth: number;
    /** The gap between the trigger and the popover (Radix's `sideOffset`). */
    sideOffset: number;
    /** How near the viewport's edge the popover may stand (Radix's `collisionPadding`). */
    collisionPadding: number;
}

/** What a popover is: how wide it is drawn, and how it is offset. */
export interface PopoverRoom {
    /** The popover's width in rem, as its width class draws it (`w-96` is 24). */
    widthRem: number;
    sideOffset: number;
    collisionPadding: number;
}

/** Whether a popover of that width fits between its trigger and the viewport's right edge. */
export function fitsBeside(room: BesideRoom): boolean {
    return room.triggerRight + room.sideOffset + room.contentWidth + room.collisionPadding <= room.viewportWidth;
}

/**
 * The width Radix measures collisions against. jsdom lays nothing out and
 * answers 0, and so does a document mid-load: the window's own width then.
 */
function viewportWidth(): number {
    return document.documentElement.clientWidth || window.innerWidth;
}

/** A rem in pixels at the root's own size: 18px here, 20 under high contrast, 2px less on a phone. */
function rootFontSize(): number {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

/**
 * Which side of its trigger a popover opens on: `right` while a popover of
 * `widthRem` fits between the trigger and the viewport's edge, `bottom` where
 * it does not.
 *
 * Measured, not read off a breakpoint. Radix flips a popover only to the
 * opposite side and shifts a right-placed one only up and down, so one opened
 * to the right of the navigation rail on a phone stood mostly off screen, and
 * no viewport breakpoint can see how wide the rail is: 4rem folded, 16rem
 * open, and the phone's menu at full width. The trigger's own right edge is
 * what the popover has to fit beside, and it is read while the popover is
 * open and again whenever the window is resized meanwhile, since a phone
 * turned or a window narrowed moves the room without closing the popover.
 * The width is a rem count, resolved at the root's own size: the same `w-96`
 * is 384px on a phone, 432 on the desktop root and 480 under high contrast.
 *
 * Closed, nothing is placed: the side stands as last decided, `right` at
 * first, and is decided afresh as the popover opens.
 */
export function usePopoverSide(trigger: RefObject<HTMLElement | null>, open: boolean, room: PopoverRoom): PopoverSide {
    const [side, setSide] = useState<PopoverSide>("right");
    const { widthRem, sideOffset, collisionPadding } = room;

    useLayoutEffect(() => {
        if (!open) return;
        const measure = () => {
            const triggerRight = trigger.current?.getBoundingClientRect().right;
            if (triggerRight == null) return;
            const fits = fitsBeside({
                triggerRight,
                viewportWidth: viewportWidth(),
                contentWidth: widthRem * rootFontSize(),
                sideOffset,
                collisionPadding,
            });
            setSide(fits ? "right" : "bottom");
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open, trigger, widthRem, sideOffset, collisionPadding]);

    return side;
}
